// SCR-STAFF-1001 — سلامت اتصال‌ها و Call Log (US-INT-030، 031، 032؛ AP-INT-02): به ازای هر اتصال وضعیت مدار، نرخ موفقیت، میانگین و
// صدک ۹۵ زمان پاسخ در ۱ و ۲۴ ساعت، طول صف و فراخوان‌های ناموفق؛ هشدارهای فعال (قطع طولانی، رشد Dead Letter، انقضای اعتبارنامه)
// و آخرین فراخوان‌ها. خط مشترک پلتفرم با برچسب «خط مشترک» (AC-INT-020.1).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type EndpointHealthView,
  type IntegrationAlertView,
  type IntegrationCallView,
} from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, JalaliDate, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router';
import { AdapterName, CircuitChip, HealthWindowText, intKeys, useAdapterTitle } from './common';

export const SCR_STAFF_1001 = 'SCR-STAFF-1001';
export const DEAD_LETTERS_PATH = '/settings/integrations/dead-letters';

export function IntegrationHealthPage() {
  const { t } = useTranslation('int');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const adapterTitle = useAdapterTitle();

  const health = useQuery({
    queryKey: intKeys.health(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/integration/health')),
    refetchInterval: 60_000,
  });
  const calls = useQuery({
    queryKey: intKeys.calls(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/integration/calls', { params: { query: { limit: 20 } } })),
  });

  const columns: DataTableColumn<EndpointHealthView>[] = [
    {
      id: 'adapter',
      headerKey: 'int:health.columns.adapter',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <AdapterName code={row.adapterCode} isSample={row.isSample} />
          {row.isPlatform ? <Chip size="small" label={t('int:health.shared')} data-testid="health-shared" /> : null}
        </Stack>
      ),
    },
    {
      id: 'circuit',
      headerKey: 'int:health.columns.circuit',
      cell: (row) => (
        <span data-testid={`health-circuit-${row.endpointId}`} data-state={row.circuitState}>
          <CircuitChip state={row.circuitState} />
        </span>
      ),
    },
    { id: 'hour', headerKey: 'int:health.columns.hour', cell: (row) => <HealthWindowText value={row.lastHour} /> },
    { id: 'day', headerKey: 'int:health.columns.day', cell: (row) => <HealthWindowText value={row.lastDay} /> },
    { id: 'queue', headerKey: 'int:health.columns.queue', cell: (row) => faDigits(Number(row.queueLength)) },
    {
      id: 'dead_letters',
      headerKey: 'int:health.columns.dead_letters',
      cell: (row) => (
        <span data-testid={`health-dead-letters-${row.endpointId}`}>{faDigits(Number(row.openDeadLetters))}</span>
      ),
    },
  ];

  const callColumns: DataTableColumn<IntegrationCallView>[] = [
    { id: 'time', headerKey: 'int:health.call_columns.time', cell: (row) => <JalaliDate value={row.createdAt} withTime /> },
    {
      id: 'adapter',
      headerKey: 'int:health.call_columns.adapter',
      cell: (row) => <AdapterName code={row.adapterCode} isSample={row.isSample} />,
    },
    { id: 'operation', headerKey: 'int:health.call_columns.operation', cell: (row) => <bdi dir="ltr">{row.operation}</bdi> },
    { id: 'status', headerKey: 'int:health.call_columns.status', cell: (row) => <bdi dir="ltr">{row.status}</bdi> },
    {
      id: 'result',
      headerKey: 'int:health.call_columns.result',
      cell: (row) => <bdi dir="ltr">{row.resultCode ?? row.errorCategory ?? '—'}</bdi>,
    },
    {
      id: 'duration',
      headerKey: 'int:health.call_columns.duration',
      cell: (row) => (row.durationMs === null ? '—' : `${faDigits(Number(row.durationMs))} ms`),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_1001}
      titleKey="int:health.title"
      subtitleKey="int:health.subtitle"
      actions={
        access.can('int.dead_letter.manage') ? (
          <Button component={RouterLink} to={DEAD_LETTERS_PATH} data-testid="health-dead-letters-link">
            {t('int:health.dead_letters_link')}
          </Button>
        ) : undefined
      }
      filters={<AlertList alerts={health.data?.alerts} adapterTitle={adapterTitle} />}
    >
      <DataTable
        captionKey="int:health.caption"
        columns={columns}
        rows={health.data?.endpoints}
        getRowId={(row) => row.endpointId}
        isLoading={health.isPending}
        error={health.error}
        onRetry={() => void health.refetch()}
        emptyTitleKey="int:connections.empty"
      />
      <Stack spacing={1} sx={{ mt: 3 }}>
        <Typography variant="h6" component="h2">
          {t('int:health.calls_title')}
        </Typography>
        <DataTable
          captionKey="int:health.calls_caption"
          columns={callColumns}
          rows={calls.data?.items}
          getRowId={(row) => row.id}
          isLoading={calls.isPending}
          error={calls.error}
          onRetry={() => void calls.refetch()}
          emptyTitleKey="int:health.no_calls"
        />
      </Stack>
    </ListPage>
  );
}

/** هشدارهای فعال (US-INT-032). */
export function AlertList({
  alerts,
  adapterTitle,
}: {
  alerts: readonly IntegrationAlertView[] | undefined;
  adapterTitle: (code: string | null | undefined) => string;
}) {
  const { t } = useTranslation('int');
  if (!alerts) {
    return null;
  }
  return (
    <Stack spacing={1} data-testid="health-alerts">
      <Typography variant="subtitle1" component="h2">
        {t('int:health.alerts_title')}
      </Typography>
      {alerts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('int:health.no_alerts')}
        </Typography>
      ) : (
        alerts.map((alert) => (
          <Alert
            key={alert.id}
            severity={alert.kind === 'CircuitOpen' ? 'error' : 'warning'}
            data-testid={`health-alert-${alert.kind}`}
          >
            {t(`int:health.alert.${alert.kind}`, {
              adapter: adapterTitle(alert.adapterCode),
              value: faDigits(Number(alert.value)),
              defaultValue: alert.kind,
            })}
          </Alert>
        ))
      )}
    </Stack>
  );
}
